import React, { useState } from 'react';
import { PageHeader } from 'src/components/common/PageHeader';
import { useNavigate } from 'react-router-dom';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Alert as ErrorAlert, AlertDescription } from 'src/components/ui/alert';
import { Alert } from 'src/components/common/Alert';
import { Eye, EyeOff, Lock } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { useToast } from 'src/hooks/use-toast';
import { useChangePasswordMutation } from 'src/services/api/queries/userQueries';

const ChangePassword = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const changePasswordMutation = useChangePasswordMutation();
  const [oldPassword, setOldPassword] = useState('');
  const [oldPasswordError, setOldPasswordError] = useState('');
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [showPasswordTooltip, setShowPasswordTooltip] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validatePassword = (value) => {
    const hasMinLength = value.length >= 8;
    const hasLowerCase = /[a-z]/.test(value);
    const hasUpperCase = /[A-Z]/.test(value);
    const hasNumber = /\d/.test(value);
    const hasSpecialChar = /[@$!%*?&]/.test(value);
    return (
      hasMinLength &&
      hasLowerCase &&
      hasUpperCase &&
      hasNumber &&
      hasSpecialChar
    );
  };

  const handleOldPasswordChange = (e) => {
    setOldPassword(e.target.value);
    setOldPasswordError('');
    setError('');
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    setPasswordError('');
    setError('');
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    setConfirmPasswordError('');
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let valid = true;
    setError('');
    setOldPasswordError('');
    setPasswordError('');
    setConfirmPasswordError('');

    if (!oldPassword) {
      setOldPasswordError('Current password is required');
      valid = false;
    }
    if (!password) {
      setPasswordError('New password is required');
      valid = false;
    } else if (!validatePassword(password)) {
      setPasswordError(
        'Password must be at least 8 characters, include uppercase, lowercase, number, and special character.'
      );
      valid = false;
    }
    if (!confirmPassword) {
      setConfirmPasswordError('Please confirm your new password');
      valid = false;
    } else if (password !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match');
      valid = false;
    }
    if (oldPassword && password && oldPassword === password) {
      setPasswordError('New password must be different from current password');
      valid = false;
    }
    if (!valid) return;

    setIsLoading(true);
    try {
      await changePasswordMutation.mutateAsync({
        current_password: oldPassword,
        new_password: password,
        confirm_password: confirmPassword,
      });
      setShowSuccessAlert(true);
      setOldPassword('');
      setPassword('');
      setConfirmPassword('');
      toast({
        message: 'Password changed successfully',
      });
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        'An error occurred while changing password. Please try again.';
      setError(errorMessage);
      toast({
        message: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAlertClose = () => {
    setShowSuccessAlert(false);
    navigate('/login');
  };

  return (
    <>
      <PageHeader title="Change Password" />
      <div className="flex justify-center bg-muted min-h-[calc(100vh-3.4rem)]">
        <div className="bg-card p-8 rounded-lg border border-border w-full max-w-md h-fit mt-2">
          <div className="flex items-start mb-6">
            <div className="h-10 w-10 rounded-xl bg-brand-bg-subtle flex items-center justify-center text-primary">
              <Lock className="h-5 w-5" />
            </div>
            <div>
              <p className="text-md font-semibold text-foreground-strong">
                Change your password
              </p>
              <p className="text-xs text-slate-600 mt-1 ml-4">
                Please enter your password details
              </p>
            </div>
          </div>

          {error && (
            <ErrorAlert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </ErrorAlert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <div className="relative">
                <Input
                  type={showOldPassword ? 'text' : 'password'}
                  placeholder="Current Password"
                  value={oldPassword}
                  onChange={handleOldPasswordChange}
                  className={`h-12 ${oldPasswordError ? 'border-red-500' : ''}`}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowOldPassword(!showOldPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  tabIndex={-1}
                >
                  {showOldPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {oldPasswordError && (
                <p className="text-sm text-red-500 mt-1 text-left">
                  {oldPasswordError}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <TooltipProvider>
                <Tooltip delayDuration={0} open={showPasswordTooltip}>
                  <TooltipTrigger asChild>
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="New Password"
                        value={password}
                        onChange={handlePasswordChange}
                        onFocus={() => setShowPasswordTooltip(true)}
                        onBlur={() => setShowPasswordTooltip(false)}
                        className={`h-12 ${passwordError ? 'border-red-500' : ''}`}
                        disabled={isLoading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        tabIndex={-1}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="p-2">
                    <div className="text-sm text-left">
                      Password must contain:
                      <div className="space-y-1 mt-1">
                        <div>• At least 8 characters</div>
                        <div>• One uppercase letter</div>
                        <div>• One lowercase letter</div>
                        <div>• One number</div>
                        <div>• One special character (@$!%*?&)</div>
                      </div>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              {passwordError && (
                <p className="text-sm text-red-500 mt-1 text-left">
                  {passwordError}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <div className="relative">
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm New Password"
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  className={`h-12 ${confirmPasswordError ? 'border-red-500' : ''}`}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  tabIndex={-1}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {confirmPasswordError && (
                <p className="text-sm text-red-500 mt-1 text-left">
                  {confirmPasswordError}
                </p>
              )}
            </div>

            <Button
              size="sm"
              type="submit"
              className="w-full text-xs"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  Changing Password...
                </div>
              ) : (
                'Change Password'
              )}
            </Button>
          </form>

          <Alert
            isOpen={showSuccessAlert}
            onClose={handleAlertClose}
            title="Success"
            description="Your password has been changed successfully. Please login with your new password."
            actionText="OK"
            onContinue={handleAlertClose}
          />
        </div>
      </div>
    </>
  );
};

export default ChangePassword;
